import React, { useState, useEffect, useCallback } from 'react';
import './App.css';
import Login from './Login';
import Dashboard from './Dashboard';
import Projects from './Projects';
import ProjectDetail from './ProjectDetail';
import Reports from './Reports';
import Notifications from './Notifications';
import MyTasks from './MyTasks';
import Documentation from './Documentation';
import AccessManagement from './AccessManagement';
import GlobalCalendar from './GlobalCalendar';
import Purchases from './Purchases';
import ProfileModal from './ProfileModal';
import ThemeToggle from './ThemeToggle';
import api from './api';
import { projectCode, CAN_RESET_PASSWORD_ROLES, ROLE_LABELS, userDisplayName, userInitials } from './helpers';
import AyaLogo from './AyaLogo';

function App() {
  const [user, setUser] = useState(null);
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [selectedProject, setSelectedProject] = useState(null);
  const [users, setUsers] = useState([]);
  // Barre latérale en tiroir sur mobile : fermée par défaut.
  const [menuOuvert, setMenuOuvert] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  // Tâche à ouvrir automatiquement à l'arrivée sur un projet (clic sur une
  // notification). Remise à zéro dès que la fenêtre est ouverte.
  const [tacheAOuvrir, setTacheAOuvrir] = useState(null);
  const [ongletProjet, setOngletProjet] = useState(null);
  const [erreurNavigation, setErreurNavigation] = useState('');

  useEffect(() => {
    // sessionStorage : la session disparait a la fermeture de l'onglet.
    const loggedInUser = sessionStorage.getItem('user');
    if (loggedInUser) {
      setUser(JSON.parse(loggedInUser));
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    api.get('/users').then((res) => setUsers(res.data)).catch(() => setUsers([]));
  }, [user]);

  const navigateTo = useCallback((page, project = null, onglet = null) => {
    setCurrentPage(page);
    if (project) setSelectedProject(project);
    setOngletProjet(onglet);
    setMenuOuvert(false);
    window.scrollTo({ top: 0 });
  }, []);

  // Clic sur une notification : on ouvre le projet concerné, et si la
  // notification désigne une tâche précise, sa fenêtre s'ouvre directement.
  const ouvrirDepuisNotification = useCallback(({ projectId, taskId }) => {
    setErreurNavigation('');

    const aller = (projet) => {
      setSelectedProject(projet);
      setTacheAOuvrir(taskId || null);
      setCurrentPage('project-detail');
    };

    if (projectId) {
      api
        .get(`/projects/${projectId}`)
        .then((res) => aller(res.data))
        .catch(() =>
          setErreurNavigation("Ce projet n'existe plus ou vous n'y avez pas accès.")
        );
      return;
    }

    // Notification sans projet : on retrouve le projet à partir de la tâche.
    if (taskId) {
      api
        .get(`/tasks/${taskId}`)
        .then((res) => api.get(`/projects/${res.data.project_id}`))
        .then((res) => aller(res.data))
        .catch(() =>
          setErreurNavigation("Cette tâche n'existe plus.")
        );
    }
  }, []);

  if (!user) {
    return <Login onLogin={setUser} />;
  }

  // Fiche complète de la personne connectée (nom complet saisi dans Accès).
  const profil = users.find((u) => u.id === user.id) || user;

  const handleLogout = () => {
    sessionStorage.removeItem('user');
    sessionStorage.removeItem('token');
    setUser(null);
  };

  const handleProjectChanged = (updated) => {
    if (!updated) {
      navigateTo('projects');
    } else {
      setSelectedProject(updated);
    }
  };

  const NAV_ITEMS = [
    { key: 'dashboard', label: 'Tableau de bord', color: '#517997' },
    { key: 'projects', label: 'Projets', color: '#C19E54' },
    { key: 'my-tasks', label: 'Mes tâches', color: '#4CAF50' },
    { key: 'calendar', label: 'Calendrier', color: '#2E6DA4' },
    { key: 'purchases', label: 'Achats', color: '#C19E54' },
    { key: 'documentation', label: 'Documentation', color: '#8CA0B3' },
    { key: 'reports', label: 'Rapports', color: '#1B2D4D' },
  ];

  // Les directeurs administrent les comptes ; les chefs de projet accèdent à la
  // même page mais n'y voient que la réinitialisation des mots de passe.
  if (CAN_RESET_PASSWORD_ROLES.includes(user.role)) {
    NAV_ITEMS.push({ key: 'access', label: 'Accès', color: '#D9534F' });
  }

  const titleForPage = () => {
    if (currentPage === 'dashboard') return 'Vue d’ensemble des projets';
    if (currentPage === 'projects') return 'Portefeuille de projets';
    if (currentPage === 'project-detail') return selectedProject?.name || '';
    if (currentPage === 'my-tasks') return 'Mes tâches';
    if (currentPage === 'calendar') return 'Calendrier des échéances';
    if (currentPage === 'purchases') return 'Suivi des demandes d’achat';
    if (currentPage === 'documentation') return 'Documentation & procédures';
    if (currentPage === 'reports') return 'Rapports & analyses';
    if (currentPage === 'access') return 'Gestion des accès';
    return '';
  };

  const breadcrumbForPage = () => {
    if (currentPage === 'dashboard') return 'PILOTAGE';
    if (currentPage === 'projects') return 'PILOTAGE / PROJETS';
    if (currentPage === 'project-detail') return 'PROJETS / ' + (selectedProject ? projectCode(selectedProject.id) : '');
    if (currentPage === 'my-tasks') return 'PILOTAGE / MES TÂCHES';
    if (currentPage === 'calendar') return 'PILOTAGE / CALENDRIER';
    if (currentPage === 'purchases') return 'PILOTAGE / ACHATS';
    if (currentPage === 'documentation') return 'PILOTAGE / DOCUMENTATION';
    if (currentPage === 'reports') return 'PILOTAGE / RAPPORTS';
    if (currentPage === 'access') return 'PILOTAGE / ACCÈS';
    return '';
  };

  return (
    <div className="app">
      <aside className={`sidebar ${menuOuvert ? 'ouverte' : ''}`} aria-label="Navigation principale">
        <div className="logo">
          <AyaLogo className="logo-mark" />
          VIGIE
        </div>

        <div className="nav-section-label">Pilotage</div>
        <nav className="nav-menu">
          {NAV_ITEMS.map((item) => {
            const actif = currentPage === item.key || (item.key === 'projects' && currentPage === 'project-detail');
            return (
              <button
                key={item.key}
                type="button"
                className={`nav-item ${actif ? 'active' : ''}`}
                aria-current={actif ? 'page' : undefined}
                onClick={() => navigateTo(item.key)}
              >
                <span className="nav-dot" style={{ background: item.color }} aria-hidden="true" /> {item.label}
              </button>
            );
          })}
        </nav>

        <button
          type="button"
          className="sidebar-user"
          style={{ cursor: 'pointer', background: 'none', border: 'none', width: '100%', textAlign: 'left', fontFamily: 'inherit' }}
          onClick={() => setShowProfile(true)}
          aria-label="Ouvrir mon profil"
        >
          <div className="avatar avatar-lg" aria-hidden="true">{userInitials(profil)}</div>
          <div>
            <div className="sidebar-user-name">{userDisplayName(profil)}</div>
            <div className="sidebar-user-role">{ROLE_LABELS[user.role] || user.role}</div>
          </div>
        </button>
      </aside>
      <div className={`sidebar-fond ${menuOuvert ? 'visible' : ''}`} onClick={() => setMenuOuvert(false)} aria-hidden="true" />

      {showProfile && <ProfileModal user={user} users={users} onClose={() => setShowProfile(false)} />}

      <main className="main-content">
        <header className="header">
          <div className="header-gauche">
            <button
              type="button"
              className="menu-burger"
              aria-label={menuOuvert ? 'Fermer le menu' : 'Ouvrir le menu'}
              aria-expanded={menuOuvert}
              onClick={() => setMenuOuvert(!menuOuvert)}
            >
              ☰
            </button>
            <div style={{ minWidth: 0 }}>
              <div className="breadcrumb">{breadcrumbForPage()}</div>
              <h1 className="page-title">{titleForPage()}</h1>
            </div>
          </div>
          <div className="header-right">
            <input
              className="search-input"
              placeholder="Rechercher un projet..."
              value={searchQuery}
              onChange={(e) => {
                const value = e.target.value;
                setSearchQuery(value);
                if (value.trim() && currentPage !== 'dashboard' && currentPage !== 'projects') {
                  navigateTo('projects');
                }
              }}
            />
            <ThemeToggle compact />
            <Notifications userId={user.id} onOuvrir={ouvrirDepuisNotification} />
            <button className="btn btn-sm" onClick={handleLogout} title="Se déconnecter de VIGIE">Déconnexion</button>
          </div>
        </header>

        <section className="content">
          {erreurNavigation && (
            <div className="error-message" style={{ marginBottom: 16 }}>{erreurNavigation}</div>
          )}
          {currentPage === 'dashboard' && (
            <Dashboard
              onSelectProject={(p) => navigateTo('project-detail', p)}
              onVoirPortefeuille={() => navigateTo('projects')}
              users={users}
              searchQuery={searchQuery}
            />
          )}
          {currentPage === 'projects' && (
            <Projects
              onSelectProject={(p, onglet) => navigateTo('project-detail', p, onglet)}
              users={users}
              searchQuery={searchQuery}
            />
          )}
          {currentPage === 'project-detail' && selectedProject && (
            <ProjectDetail
              project={selectedProject}
              users={users}
              currentUser={user}
              onBack={() => navigateTo('projects')}
              onProjectChanged={handleProjectChanged}
              openTaskId={tacheAOuvrir}
              onTaskOpened={() => setTacheAOuvrir(null)}
              ongletInitial={ongletProjet}
            />
          )}
          {currentPage === 'my-tasks' && (
            <MyTasks users={users} currentUser={user} onOpenProject={(p) => navigateTo('project-detail', p)} />
          )}
          {currentPage === 'calendar' && (
            <GlobalCalendar users={users} currentUser={user} onOpenProject={(p, onglet) => navigateTo('project-detail', p, onglet)} />
          )}
          {currentPage === 'purchases' && (
            <Purchases
              users={users}
              currentUser={user}
              onOpenProject={(p) => (p ? navigateTo('project-detail', p) : navigateTo('projects'))}
            />
          )}
          {currentPage === 'documentation' && <Documentation users={users} currentUser={user} />}
          {currentPage === 'reports' && <Reports />}
          {currentPage === 'access' && CAN_RESET_PASSWORD_ROLES.includes(user.role) && (
            <AccessManagement currentUser={user} />
          )}
        </section>
      </main>
    </div>
  );
}

export default App;
